/**
 * TeamDialog - Universal Create/Edit Team Dialog with AI Auto-Fill
 * 
 * Features:
 * - Large description/brief textarea at top
 * - "Auto-fill from description" magic button (AI extracts metadata)
 * - All quality-relevant fields: team type, location, location model,
 *   start/end dates, success criteria
 * - Create mode (no team) vs Edit mode (pre-filled)
 */

import React, { useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';

interface TeamDialogProps {
  team?: {
    id: number;
    name: string;
    project_id: number | null;
    status?: string;
    team_type?: string;
    start_date?: string;
    end_date?: string;
    location?: string;
    location_model?: string;
    expected_deliverables?: string;
    success_criteria?: string;
  } | null;
  defaultProjectId?: number;
  onSave: (data: {
    name: string;
    project_id: number | null;
    status?: string;
    team_type?: string;
    start_date?: string;
    end_date?: string;
    location?: string;
    location_model?: string;
    expected_deliverables?: string;
    success_criteria?: string;
  }) => void;
  onClose: () => void;
}

const TEAM_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'functional', label: 'Functional Team' },
  { value: 'cross_functional', label: 'Cross-functional Team' },
  { value: 'squad', label: 'Product Squad' },
  { value: 'task_force', label: 'Task Force' },
];

const LOCATION_MODELS = [
  { value: '', label: 'Select model...' },
  { value: 'onsite', label: 'Onsite' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
  { value: 'distributed', label: 'Distributed' },
];

const TEAM_STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'complete', label: 'Complete' },
  { value: 'archived', label: 'Archived' },
];

// API helper for AI extraction
const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

function getAuthToken(): string | null {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try { return JSON.parse(authData).state.token || null; } catch { return null; }
  }
  return null;
}

export const TeamDialog: React.FC<TeamDialogProps> = ({
  team,
  defaultProjectId,
  onSave,
  onClose,
}) => {
  const { projects } = useProjectStore();
  const isEditMode = !!team;

  // Form state
  const [name, setName] = useState(team?.name || '');
  const [projectId, setProjectId] = useState<number>(
    team?.project_id || defaultProjectId || 0
  );
  const [status, setStatus] = useState(team?.status || 'planning');
  const [description, setDescription] = useState(team?.expected_deliverables || '');
  const [teamType, setTeamType] = useState(team?.team_type || '');
  const [location, setLocation] = useState(team?.location || '');
  const [locationModel, setLocationModel] = useState(team?.location_model || '');
  const [startDate, setStartDate] = useState(team?.start_date?.substring(0, 7) || '');
  const [endDate, setEndDate] = useState(team?.end_date?.substring(0, 7) || '');
  const [successCriteria, setSuccessCriteria] = useState(team?.success_criteria || '');
  const [error, setError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [aiFieldsSet, setAiFieldsSet] = useState(false);


  // AI Auto-fill handler
  const handleAutoFill = async () => {
    if (description.trim().length < 20) return;
    
    setExtracting(true);
    setError('');
    
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE}/teams/extract-metadata`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ description: description.trim() }),
      });

      if (!response.ok) {
        throw new Error('AI extraction failed');
      }

      const metadata = await response.json();

      // Fill in extracted fields (only if they were extracted)
      if (metadata.name_suggestion && !name) {
        setName(metadata.name_suggestion);
      }
      if (metadata.team_type) setTeamType(metadata.team_type);
      if (metadata.location) setLocation(metadata.location);
      if (metadata.location_model) setLocationModel(metadata.location_model);
      if (metadata.start_date) setStartDate(metadata.start_date);
      if (metadata.end_date) setEndDate(metadata.end_date);
      if (metadata.success_criteria) setSuccessCriteria(metadata.success_criteria);

      setAiFieldsSet(true);
    } catch (err: any) {
      setError('AI extraction failed. Please fill fields manually.');
      console.error('Auto-fill error:', err);
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Team name is required');
      return;
    }
    onSave({
      name: trimmedName,
      project_id: projectId || null,
      status: status || undefined,
      team_type: teamType || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      location: location || undefined,
      location_model: locationModel || undefined,
      expected_deliverables: description.trim() || undefined,
      success_criteria: successCriteria.trim() || undefined,
    });
  };

  const canAutoFill = description.trim().length >= 20 && !extracting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-[90vw] max-w-[900px] max-h-[90vh] overflow-y-auto bg-slate-800 border border-gray-700 rounded-xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-slate-800 rounded-t-xl z-10">
          <h2 className="text-white font-bold text-lg">
            {isEditMode ? `Edit Team: ${team!.name}` : 'Create New Team'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-lg">x</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Team Name */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Team Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              placeholder="Enter team name..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              autoFocus
            />
          </div>

          {/* Project */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Project</label>
            <select
              value={projectId}
              onChange={e => setProjectId(parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
            >
              <option value={0}>Unassigned</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Status</label>
            <select
              data-testid="team-dialog-status"
              aria-label="Status"
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
            >
              {TEAM_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Separator */}
          <div className="border-t border-gray-700" />

          {/* Description / Brief (large textarea) */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Description / Brief</label>
            <textarea
              value={description}
              onChange={e => { setDescription(e.target.value); setAiFieldsSet(false); }}
              placeholder="Describe the team purpose, requirements, timeline... Paste a project brief here. The AI will extract dates, location, team type, and success criteria."
              rows={8}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none resize-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Magic auto-fill button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleAutoFill}
              disabled={!canAutoFill}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                aiFieldsSet
                  ? 'bg-green-600/15 border border-green-500 text-green-400'
                  : canAutoFill
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {extracting ? (
                <>
                  <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                  Extracting...
                </>
              ) : aiFieldsSet ? (
                '* Fields extracted by AI'
              ) : (
                '* Auto-fill from description'
              )}
            </button>
          </div>

          {/* Separator */}
          <div className="border-t border-gray-700" />

          {/* Team Type + Location Model */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">Team Type</label>
              <select
                value={teamType}
                onChange={e => setTeamType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              >
                {TEAM_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">Location Model</label>
              <select
                value={locationModel}
                onChange={e => setLocationModel(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              >
                {LOCATION_MODELS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Location</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g., Prague, Berlin, Remote"
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Start Date + End Date */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">Start Date</label>
              <input
                type="month"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1">End Date</label>
              <input
                type="month"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
          </div>

          {/* Success Criteria */}
          <div>
            <label className="block text-gray-400 text-xs mb-1">Success Criteria</label>
            <textarea
              value={successCriteria}
              onChange={e => setSuccessCriteria(e.target.value)}
              placeholder="What defines success for this team?"
              rows={2}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 text-sm outline-none resize-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Tip (create mode only) */}
          {!isEditMode && !description && (
            <p className="text-gray-600 text-[11px] border-t border-gray-700 pt-3">
              Tip: Write a description and click "Auto-fill" to let AI extract the metadata fields.
            </p>
          )}

          {/* Error */}
          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {isEditMode ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
